import { ReactNode } from 'react'

import { Button, Divider, Stack, Typography } from '@mui/material'
import { getTestId } from '@utils/helpers'

type Props = {
	label: string
	dirty?: boolean
	onCancel?: () => void
	onSave?: () => void
	submit?: boolean // if true, the save button will submit the form
	formId?: string // if submit is true, this is the id of the form to submit
	hideSave?: boolean
	hideCancel?: boolean
	children?: ReactNode
}

/**
 * Header for drawers with title, and either custom actions or the default Cancel/Save actions.
 */
export default function DrawerHeader({
	label,
	dirty = false,
	onCancel = () => {},
	onSave = undefined,
	submit = false,
	formId = undefined,
	hideSave = false,
	hideCancel = false,
	children = null,
	...rest
}: Props) {
	const testId = getTestId(rest, label)

	if (submit && !formId) {
		throw new Error('DrawerHeader: formId is required when submit is true')
	}

	return (
		<Stack>
			<Stack
				direction="row"
				sx={{ width: '100%', mb: 2, justifyContent: 'space-between' }}
				data-testid={`drawer-${testId}`}
			>
				<Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
					{label}
				</Typography>
				<Stack spacing={2} direction="row" sx={{ justifyContent: 'flex-end' }}>
					{children ?? (
						<>
							{!hideCancel && (
								<Button
									variant="text"
									onClick={onCancel}
									color="secondary"
									data-testid={`${testId}-cancel`}
								>
									Cancel
								</Button>
							)}
							{!hideSave && (
								<Button
									type={submit ? 'submit' : 'button'}
									form={formId}
									onClick={onSave}
									disabled={!dirty}
									variant="contained"
									data-testid={`${testId}-save`}
								>
									Save
								</Button>
							)}
						</>
					)}
				</Stack>
			</Stack>
			<Divider />
		</Stack>
	)
}
